// Penerjemah error RPC/Postgres/PostgREST ke pesan Bahasa Indonesia yang aman
// ditampilkan ke reseller/admin/kasir (usulan bughunt H2, docs/bughunt/H2.md).
//
// RPC di repo ini SUDAH menulis `RAISE EXCEPTION` berbahasa Indonesia manusiawi
// (mis. "Saldo tidak mencukupi") -- helper ini TIDAK menimpa pesan semacam itu,
// hanya menerjemahkan error level Postgres/PostgREST yang belum sempat "dikurasi"
// RPC-nya sendiri (constraint tak divalidasi lebih dulu, RLS, jaringan).

export type RpcErrorKind =
  | "validasi"
  | "duplikat"
  | "terpakai"
  | "invalid"
  | "izin"
  | "fungsi_belum_ada"
  | "jaringan"
  | "waktu_habis"
  | "tak_dikenal";

export type TerjemahanError = {
  kind: RpcErrorKind;
  /** Aman ditampilkan ke pengguna non-teknis -- KECUALI kind "fungsi_belum_ada" (lihat isRpcMissingError). */
  pesan: string;
  /** Pesan asli -- console.error/telemetry saja, jangan dirender ke pengguna. */
  mentah: string;
};

type ErrorLike = {
  code?: string;
  message?: string;
  details?: string;
};

const DEFAULT_MESSAGE = "Terjadi kesalahan. Coba lagi. Kalau berulang, hubungi admin.";

const MISSING_FN_CODES = new Set(["PGRST202", "PGRST205", "42883", "42P01"]);

/** Kode unique-constraint dikenal → pesan spesifik, bukan "Data ini sudah ada" generik. */
const KNOWN_UNIQUE_CONSTRAINTS: ReadonlyArray<readonly [string, string]> = [
  ["stores_custom_domain_key", "Domain ini sudah dipakai toko lain."]
];

const NETWORK_MESSAGE_RE = /failed to fetch|networkerror|load failed|timed? ?out|offline/i;
const PERMISSION_MESSAGE_RE = /permission denied|row-level security/i;

/**
 * Benar untuk pesan izin MENTAH Postgres/PostgREST ("permission denied …",
 * "… row-level security …") — bukan kalimat kurasi RPC yang kebetulan ber-42501.
 */
export function isRawPermissionMessage(message: string): boolean {
  return PERMISSION_MESSAGE_RE.test(message);
}
/** Pesan CHECK constraint bawaan Postgres (bukan RAISE kurasi RPC). */
const RAW_CHECK_MESSAGE_RE = /violates check constraint/i;
/** Kode mesin berawalan `HURUF_BESAR:` (mis. `VOUCHER_INVALID:quota`). */
const MACHINE_CODE_RE = /^[A-Z][A-Z0-9_]+:/;

function toErrorLike(error: unknown): ErrorLike | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as Record<string, unknown>;
  return {
    code: typeof e.code === "string" ? e.code : undefined,
    message: typeof e.message === "string" ? e.message : undefined,
    details: typeof e.details === "string" ? e.details : undefined
  };
}

function extractMentah(error: unknown, e: ErrorLike | undefined): string {
  if (e?.message) return e.message;
  if (error instanceof Error) return error.message;
  if (typeof error === "string" && error) return error;
  return "Kesalahan tidak diketahui.";
}

/**
 * Benar untuk error "fungsi/relasi belum ada di DB" (PGRST202/PGRST205/42883/42P01
 * atau teks setara) -- BUKAN kegagalan nyata, sinyal bagi pemanggil untuk jatuh ke
 * jalur cadangan (pola yang sudah ada di variantSave.ts/cartActionSubmit.ts/
 * adminCart.ts/expenses.ts, disatukan di sini untuk pemanggil BARU).
 */
export function isRpcMissingError(error: unknown): boolean {
  const e = toErrorLike(error);
  if (!e) return false;
  if (e.code && MISSING_FN_CODES.has(e.code)) return true;
  const msg = (e.message ?? "").toLowerCase();
  if (msg.includes("could not find the function")) return true;
  if (msg.includes("function") && msg.includes("does not exist")) return true;
  return msg.includes("schema cache");
}

function pesanDuplikat(e: ErrorLike): string {
  const text = `${e.message ?? ""} ${e.details ?? ""}`;
  for (const [needle, pesan] of KNOWN_UNIQUE_CONSTRAINTS) {
    if (text.includes(needle)) return pesan;
  }
  return "Data ini sudah ada, tidak boleh duplikat.";
}

/**
 * Terjemahkan error RPC/query Supabase ke pesan Indonesia yang aman ditampilkan.
 * Tidak pernah melempar -- input apa pun (PostgrestError, Error, string, undefined)
 * memulangkan hasil yang aman.
 *
 * `kind: "fungsi_belum_ada"` BUKAN untuk ditampilkan ke pengguna -- cek
 * `isRpcMissingError()` (atau `kind`) dan tempuh jalur cadangan pemanggil.
 */
export function terjemahkanErrorRpc(error: unknown): TerjemahanError {
  const e = toErrorLike(error);
  const mentah = extractMentah(error, e);

  if (isRpcMissingError(error)) {
    return { kind: "fungsi_belum_ada", pesan: DEFAULT_MESSAGE, mentah };
  }

  const code = e?.code ?? "";
  if (code === "23505") return { kind: "duplikat", pesan: pesanDuplikat(e ?? {}), mentah };
  if (code === "23503") {
    return {
      kind: "terpakai",
      pesan: "Data ini masih terkait data lain, tidak bisa dihapus/diubah.",
      mentah
    };
  }
  if (code === "23514") {
    // RPC repo ini memakai `errcode = 'check_violation'` untuk ±40 penolakan
    // yang pesannya SUDAH dikurasi ("Nota ini masih mengunci 1 pcs yang
    // tercatat lunas — batalkan pelunasannya dulu", "Stok tidak cukup …").
    // Dulu semuanya ditimpa pesan generik — admin galleryhasanah hanya melihat
    // "tidak memenuhi aturan data" saat Batalkan nota ditolak (2026-09-30).
    // Yang tetap disamarkan: pesan constraint mentah Postgres dan kode mesin
    // (`VOUCHER_INVALID:…`, `PROMO_QUOTA:…`) — keduanya bukan untuk manusia.
    const dikurasi = e?.message ?? "";
    if (dikurasi && !RAW_CHECK_MESSAGE_RE.test(dikurasi) && !MACHINE_CODE_RE.test(dikurasi)) {
      return { kind: "validasi", pesan: dikurasi, mentah };
    }
    return { kind: "invalid", pesan: "Nilai yang dikirim tidak memenuhi aturan data.", mentah };
  }
  if (code === "42501" || PERMISSION_MESSAGE_RE.test(mentah)) {
    return { kind: "izin", pesan: "Akun ini tidak punya izin untuk aksi ini.", mentah };
  }
  // 57014 = statement_timeout server (8 s untuk authenticated), BUKAN jaringan:
  // teksnya ("canceling statement due to statement timeout") dulu tertangkap
  // NETWORK_MESSAGE_RE lalu menyuruh memeriksa internet. Transaksi PostgREST
  // satu permintaan itu digulung utuh — tak ada yang tersimpan.
  if (code === "57014") {
    return {
      kind: "waktu_habis",
      pesan: "Server kehabisan waktu memproses permintaan ini. Belum ada yang tersimpan — coba lagi.",
      mentah
    };
  }
  if (NETWORK_MESSAGE_RE.test(mentah)) {
    return {
      kind: "jaringan",
      pesan: "Koneksi terputus. Periksa internet lalu coba lagi.",
      mentah
    };
  }
  // RAISE EXCEPTION custom (tanpa kode PostgREST/constraint dikenal) -- RPC repo
  // ini sudah menulis pesan Indonesia manusiawi, teruskan apa adanya.
  if (!code || code === "P0001") {
    return { kind: "validasi", pesan: mentah || DEFAULT_MESSAGE, mentah };
  }

  return { kind: "tak_dikenal", pesan: DEFAULT_MESSAGE, mentah };
}
